'use client'

import { useEffect, useRef } from 'react'
import CatalogCategoryIcon from './CatalogCategoryIcon'

interface CatalogTab {
  slug: string
  label: string
  href: string
}

interface CatalogTabsProps {
  tabs: CatalogTab[]
  /** null while searching, when no tab is showing */
  active: string | null
  onChange: (slug: string) => void
}

export default function CatalogTabs({ tabs, active, onChange }: CatalogTabsProps) {
  const stripRef = useRef<HTMLDivElement>(null)

  // Below lg a category URL's tab can start past the right edge, so the strip scrolls to it
  useEffect(() => {
    const strip = stripRef.current
    const tab = strip?.querySelector<HTMLElement>('[aria-current="page"]')
    if (!strip || !tab) return
    if (tab.offsetLeft + tab.offsetWidth > strip.scrollLeft + strip.clientWidth || tab.offsetLeft < strip.scrollLeft) {
      strip.scrollLeft = tab.offsetLeft
    }
  }, [active])

  return (
    <nav
      aria-label="Catalog categories"
      className="relative -mx-4 mb-6 border-b border-primary-ink-10 after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:w-12 after:bg-gradient-to-l after:from-secondary-gray-10 lg:sticky lg:top-28 lg:mx-0 lg:mb-0 lg:self-start lg:border-b-0 lg:after:hidden">
      <div
        ref={stripRef}
        className="relative flex min-w-0 gap-1 overflow-x-auto px-1 scrollbar-none lg:flex-col lg:gap-1 lg:overflow-visible lg:px-0">
        <div className="hidden lg:mb-1 lg:block lg:px-3 lg:text-[13px] lg:text-primary-ink-60">Categories</div>
        {tabs.map((tab) => (
          <a
            key={tab.slug}
            href={tab.href}
            onClick={(e) => {
              // Modified clicks keep the browser's open-in-new-tab behavior
              if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
              e.preventDefault()
              onChange(tab.slug)
            }}
            aria-current={active === tab.slug ? 'page' : undefined}
            className={`group flex shrink-0 items-center whitespace-nowrap border-b-2 px-3 py-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-green-85 motion-reduce:transition-none lg:h-9 lg:gap-3 lg:rounded-lg lg:border-b-0 lg:px-3 lg:py-0 lg:text-[15px] lg:font-normal ${
              active === tab.slug
                ? 'border-primary-green-85 text-primary-ink-100 lg:bg-primary-ink-6'
                : 'border-transparent text-primary-ink-60 hover:text-primary-ink-100 lg:text-primary-ink-85 lg:hover:bg-primary-ink-6'
            }`}>
            <CatalogCategoryIcon
              slug={tab.slug}
              className={`hidden h-[18px] w-[18px] shrink-0 lg:block ${active === tab.slug ? 'text-primary-ink-100' : 'text-primary-ink-60 group-hover:text-primary-ink-85'}`}
            />
            {tab.label}
          </a>
        ))}
      </div>
    </nav>
  )
}
